export class CarouselMotion {
  constructor(opts) {
    this.count = opts.count;
    this.getEl = opts.getEl;
    this.autoDelayMs = opts.autoDelayMs ?? 4000;
    this.firstDelayMs = opts.firstDelayMs ?? this.autoDelayMs;
    this.autoplay = opts.autoplay !== false;
    this.onActiveChange = opts.onActiveChange || (() => {});
    this.onTap = opts.onTap || (() => {});

    const L = opts.layout || {};
    this.INFO_W = L.INFO_W ?? 330;
    this.PHOTO_SIDE = L.PHOTO_SIDE ?? 240;
    this.PHOTO_GROW = L.PHOTO_GROW ?? 90;
    this.H_SIDE = L.H_SIDE ?? 240;
    this.H_GROW = L.H_GROW ?? 170;
    this.GAP = L.GAP ?? 24;
    this.FIRST = this.INFO_W / 2 + (this.PHOTO_SIDE + this.PHOTO_GROW) / 2 + this.GAP + this.PHOTO_SIDE / 2;
    this.STEP = this.PHOTO_SIDE + this.GAP;

    this.getElMobile = opts.getElMobile || (() => null);
    this.isMobile = false;
    this.STEP_M = opts.STEP_M ?? 316; // mobile: card 300 + gap 16

    this.p = 0;
    this.target = 0;
    this.active = 0;
    this.dragging = false;
    this.hovering = false;
    this.dragDist = 0;
    this.velU = 0;
    this.lastT = null;
    this.nextAt = 0;
    this.raf = null;
    this.armed = false;
    this._pendingArm = false;
  }

  armAutoplay() { if (!this.armed) this._pendingArm = true; }

  setMobile(m) { this.isMobile = !!m; }
  stepPx() { return this.isMobile ? this.STEP_M : this.STEP; }

  start() {
    if (this.raf) return;
    const frame = (t) => {
      this.raf = requestAnimationFrame(frame);
      if (this.lastT == null) { this.lastT = t; this.nextAt = t + this.firstDelayMs; return; }
      const dt = Math.min((t - this.lastT) / 1000, 0.05);
      this.lastT = t;

      if (this._pendingArm) { this._pendingArm = false; this.armed = true; this.nextAt = t + this.firstDelayMs; }

      if (this.armed && this.autoplay && !this.dragging && !this.hovering && t >= this.nextAt) {
        this.target = Math.round(this.target) + 1;
        this.nextAt = t + this.autoDelayMs;
      }
      if (this.dragging || this.hovering) this.nextAt = t + this.autoDelayMs;

      if (!this.dragging) {
        const d = this.target - this.p;
        if (Math.abs(d) < 0.0005) this.p = this.target;
        else this.p += d * Math.min(1, dt * 6);
      }

      this.layout();

      const act = ((Math.round(this.p) % this.count) + this.count) % this.count;
      if (act !== this.active) { this.active = act; this.onActiveChange(act); }
    };
    this.raf = requestAnimationFrame(frame);
  }

  layout() {
    const N = this.count;
    if (this.isMobile) {
      for (let i = 0; i < N; i++) {
        const el = this.getElMobile(i);
        if (!el) continue;
        let off = (((i - this.p) % N) + N) % N;
        if (off > N / 2) off -= N;
        const s = Math.abs(off);
        el.style.transform = `translate(-50%,-50%) translateX(${off * this.STEP_M}px)`;
        el.style.zIndex = String(20 - Math.round(s));
        el.style.visibility = s > 1.6 ? 'hidden' : 'visible';
        el.style.opacity = String(Math.max(0.35, 1 - s * 0.65));
      }
      return;
    }
    for (let i = 0; i < N; i++) {
      const el = this.getEl(i);
      if (!el || !el.root) continue;
      let off = (((i - this.p) % N) + N) % N;
      if (off > N / 2) off -= N;
      const s = Math.abs(off);
      const a = Math.max(0, 1 - s);
      const x = Math.sign(off) * (s <= 1 ? s * this.FIRST : this.FIRST + (s - 1) * this.STEP);

      el.root.style.transform = `translate(-50%,-50%) translateX(${x}px)`;
      el.root.style.height = (this.H_SIDE + a * this.H_GROW) + 'px';
      el.root.style.zIndex = String(20 - Math.round(s));
      el.root.style.visibility = s > 2.7 ? 'hidden' : 'visible';
      if (el.info) {
        el.info.style.width = (a * this.INFO_W) + 'px';
        el.info.style.opacity = String(Math.pow(a, 1.6));
      }
      if (el.photo) el.photo.style.width = (this.PHOTO_SIDE + a * this.PHOTO_GROW) + 'px';
    }
  }

  bind(viewport) {
    this.viewport = viewport;
    this._down = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      this.dragging = true;
      this.dragDist = 0;
      this.pStart = this.p;
      this.xStart = e.clientX;
      this.lastPX = e.clientX;
      this.lastPT = performance.now();
      this.velU = 0;
      try { viewport.setPointerCapture(e.pointerId); } catch (_) {}
      viewport.style.cursor = 'grabbing';
    };
    this._move = (e) => {
      if (!this.dragging) return;
      const now = performance.now();
      const dx = e.clientX - this.lastPX;
      const dtm = Math.max(now - this.lastPT, 1);
      this.dragDist += Math.abs(dx);
      this.p = this.pStart - (e.clientX - this.xStart) / this.stepPx();
      this.velU = Math.max(-8, Math.min(8, (-dx / dtm) * 1000 / this.stepPx()));
      this.lastPX = e.clientX;
      this.lastPT = now;
    };
    this._up = (e) => {
      if (!this.dragging) return;
      this.dragging = false;
      viewport.style.cursor = 'grab';
      const stale = performance.now() - this.lastPT > 90;
      const fling = stale ? 0 : this.velU * 0.18;
      let tgt = Math.round(this.p + fling);
      tgt = Math.max(Math.round(this.p) - 2, Math.min(Math.round(this.p) + 2, tgt));
      this.target = tgt;
      if ((this.dragDist || 0) <= 8 && e && e.type === 'pointerup') this.onTap(e.clientX, e.clientY);
    };
    this._enter = () => { this.hovering = true; };
    this._leave = () => { this.hovering = false; };
    viewport.addEventListener('pointerdown', this._down);
    viewport.addEventListener('pointermove', this._move);
    viewport.addEventListener('pointerup', this._up);
    viewport.addEventListener('pointercancel', this._up);
    viewport.addEventListener('mouseenter', this._enter);
    viewport.addEventListener('mouseleave', this._leave);
  }

  goTo(i) {
    let off = (((i - this.p) % this.count) + this.count) % this.count;
    if (off > this.count / 2) off -= this.count;
    this.target = Math.round(this.p + off);
  }
  next() { this.target = Math.round(this.target) + 1; }
  prev() { this.target = Math.round(this.target) - 1; }

  wasDrag() { return (this.dragDist || 0) > 8; }

  offsetOf(i) {
    let off = (((i - this.p) % this.count) + this.count) % this.count;
    if (off > this.count / 2) off -= this.count;
    return off;
  }

  destroy() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    const v = this.viewport;
    if (v) {
      v.removeEventListener('pointerdown', this._down);
      v.removeEventListener('pointermove', this._move);
      v.removeEventListener('pointerup', this._up);
      v.removeEventListener('pointercancel', this._up);
      v.removeEventListener('mouseenter', this._enter);
      v.removeEventListener('mouseleave', this._leave);
    }
  }
}

export default CarouselMotion;
